import {
  type JiraGraphQLIssueLinkType,
  type JiraGraphQLIssueType,
  type JiraGraphQLIssueFieldResponse,
  type JiraGraphQLPriority,
  type JiraGraphQLRequestType,
  type JiraOption,
  type JiraRestProject,
} from '@canvas/atlassian-client/jira/types';

import {
  type Project,
  type IssueFieldOption,
  type IssueField,
  SupportedJiraIssueFields,
} from '../jira-service-types.ts';

export function toProject(p: JiraRestProject, base: string, cloudId?: string): Project {
  return {
    id: p.id,
    uuid: p.uuid ?? `${cloudId || 'project'}-${p.id}`,
    key: p.key,
    name: p.name,
    iconUrl: new URL(`/gateway/api/${new URL(p.avatarUrls['48x48']).pathname}`, base),
  };
}

export function toIssueFieldOption(
  n:
    | JiraGraphQLIssueType
    | JiraGraphQLRequestType
    | JiraGraphQLPriority
    | JiraGraphQLIssueLinkType
    | JiraOption
    | undefined,
  base: string,
): IssueFieldOption | undefined {
  if (!n) {
    return undefined;
  }

  return {
    label:
      ((n as JiraGraphQLIssueType | JiraGraphQLRequestType).name ||
        (n as JiraGraphQLIssueLinkType).relationName ||
        (n as JiraOption).value) ??
      'unknown',
    value:
      (n as JiraOption).optionId ||
      (n as JiraGraphQLIssueType).id ||
      (n as JiraGraphQLRequestType).key ||
      `${(n as JiraGraphQLIssueLinkType).linkTypeId}-${
        (n as JiraGraphQLIssueLinkType).direction
      }`,
    iconUrl: getAvatarUrl(
      (n as JiraGraphQLIssueType | JiraGraphQLRequestType).avatar?.large ??
        (n as JiraGraphQLPriority).iconUrl,
      base,
    ),
    info: (n as JiraGraphQLRequestType).description,
    meta: {
      issueTypeId: (n as JiraGraphQLRequestType).issueType?.issueTypeId,
    },
  };
}

const getAvatarUrl = (url: string | undefined, base: string) => {
  if (url) {
    const pathname = getPathname(url);
    return new URL(`${base}${pathname}`).href;
  }

  return undefined;
};

const getPathname = (url: string) => {
  const prefix = url.includes('/rest/api') ? '/rest/api/' : '/images/icons/';
  return `${prefix}${url.split(prefix)[1]}`;
};

const toOptionsFromField = (
  f: JiraGraphQLIssueFieldResponse,
  base: string,
): IssueFieldOption[] => {
  if (f.issueTypes) {
    return f.issueTypes.nodes.map(
      (n): IssueFieldOption => toIssueFieldOption(n, base) as IssueFieldOption,
    );
  }

  if (f.components) {
    return f.components.nodes.map((c) => ({
      label: c.name,
      value: c.id,
    }));
  }

  if (f.requestTypes) {
    const requestTypes = f.requestTypes.nodes.map(
      (n): IssueFieldOption => toIssueFieldOption(n, base) as IssueFieldOption,
    );
    return [
      {
        label: 'No request type',
        value: '',
        info: `Issues can’t be shared with customers, and may not work as expected`,
      },
      ...requestTypes,
    ];
  }

  if (f.priorities) {
    return f.priorities.nodes.map(
      (n): IssueFieldOption => toIssueFieldOption(n, base) as IssueFieldOption,
    );
  }

  if (f.issueLinkTypeRelations) {
    return f.issueLinkTypeRelations.nodes.map(
      (n): IssueFieldOption => toIssueFieldOption(n, base) as IssueFieldOption,
    );
  }

  if (f.fieldOptions) {
    return f.fieldOptions.edges
      .filter((n) => !n.node.isDisabled)
      .map((n): IssueFieldOption => toIssueFieldOption(n.node, base) as IssueFieldOption);
  }

  return [];
};

const toFieldName = (f: JiraGraphQLIssueFieldResponse): string => {
  if (f.fieldId === 'timetracking') {
    return 'Original estimate';
  }
  return f.name;
};

export function toIssueField(base: string, f: JiraGraphQLIssueFieldResponse): IssueField {
  // @ts-expect-error: __typename is a GraphQL internal property
  // eslint-disable-next-line no-underscore-dangle
  const dataType = f.__typename;
  return {
    id: f.fieldId,
    name: toFieldName(f),
    options: toOptionsFromField(f, base),
    additionalOptions: f.issueLinkTypeRelations && toOptionsFromField(f, base),
    defaultOption: toIssueFieldOption(
      f.issueType || f.requestType || f.issueLinks?.[0]?.relatedBy || f.fieldOption,
      base,
    ),
    type:
      f.searchUrl || dataType === SupportedJiraIssueFields.ATLASSIAN_TEAM
        ? 'asyncSelect'
        : 'text',
    searchUrl: f.searchUrl,
    dataType: dataType,
    fieldConfig: f.fieldConfig,
    description: f.description,
  };
}
